import { useCallback, useEffect, useState } from "react";
import { ArrowsDownUp, LinkSimple, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, EXPENSE_CATEGORIES, fmtErr, inr, today } from "../lib/api";
import { StatementUpload } from "../components/StatementUpload";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const WITHDRAW_PURPOSES = ["Expense", "Vendor Advance", "Vendor Payment", "Customer Refund", "Credit Note Refund", "Transfer to Another Account", "Owner Drawings", "Other"];
const DEPOSIT_PURPOSES = ["Customer Payment", "Vendor Refund", "Debit Note Refund", "Owner Contribution", "Transfer from Another Account", "Other Income"];
const PARTY_PURPOSE_KIND = {
  "Vendor Advance": "vendor", "Vendor Payment": "vendor", "Customer Refund": "customer", "Credit Note Refund": "customer",
  "Customer Payment": "customer", "Vendor Refund": "vendor", "Debit Note Refund": "vendor",
};
const SETTLE_PURPOSES = ["Vendor Payment", "Customer Payment"];

export default function Banking() {
  const [accounts, setAccounts] = useState([]);
  const [txns, setTxns] = useState([]);
  const [txnAccountId, setTxnAccountId] = useState("all");
  const [txnDateFrom, setTxnDateFrom] = useState("");
  const [txnDateTo, setTxnDateTo] = useState("");
  const [txnSort, setTxnSort] = useState({ key: "date", direction: "desc" });
  const [accOpen, setAccOpen] = useState(false);
  const [txnOpen, setTxnOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accForm, setAccForm] = useState({ name: "", type: "bank", account_type: "current_account", sanction_amount: "0", bank_name: "", account_number: "", ifsc: "", upi_id: "", opening_balance: "0" });
  const [txnForm, setTxnForm] = useState({ account_id: "", date: today(), type: "deposit", amount: "", purpose: "Customer Payment", expense_category: "Rent", party_id: "", doc_id: "", reference: "", notes: "" });
  const [parties, setParties] = useState([]);
  const [settings, setSettings] = useState(null);
  const [openDocs, setOpenDocs] = useState([]);
  const [linkTxn, setLinkTxn] = useState(null);
  const [linkDocs, setLinkDocs] = useState([]);
  const [linkDocId, setLinkDocId] = useState("");
  const [linkSaving, setLinkSaving] = useState(false);
  const [catTxn, setCatTxn] = useState(null);
  const [catForm, setCatForm] = useState({ purpose: "Customer Payment", expense_category: "Rent", party_id: "", doc_id: "" });
  const [catDocs, setCatDocs] = useState([]);
  const [catSaving, setCatSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, t, c, v, s] = await Promise.all([
        API.get("/banking/accounts"),
        API.get("/banking/transactions", { params: txnAccountId === "all" ? {} : { account_id: txnAccountId } }),
        API.get("/parties?type=customer"), API.get("/parties?type=vendor"), API.get("/settings"),
      ]);
      setAccounts(a.data);
      setTxns(t.data);
      setParties([...c.data, ...v.data]);
      setSettings(s.data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }, [txnAccountId]);

  useEffect(() => {
    load();
  }, [load]);

  const setAcc = (k) => (e) => setAccForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const setTxn = (k) => (e) => setTxnForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const saveAccount = async () => {
    if (!accForm.name.trim()) return toast.error("Account name required");
    if (accForm.type === "upi" && !accForm.upi_id.trim()) return toast.error("UPI ID required for a UPI account");
    if (accForm.account_type === "credit_limit" && !(Number(accForm.sanction_amount) > 0)) return toast.error("Enter a valid sanctioned amount");
    setSaving(true);
    try {
      await API.post("/banking/accounts", {
        ...accForm,
        opening_balance: Number(accForm.opening_balance) || 0,
        sanction_amount: Number(accForm.sanction_amount) || 0,
      });
      toast.success("Account added");
      setAccOpen(false);
      setAccForm({ name: "", type: "bank", account_type: "current_account", sanction_amount: "0", bank_name: "", account_number: "", ifsc: "", upi_id: "", opening_balance: "0" });
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const purposes = txnForm.type === "withdrawal" ? WITHDRAW_PURPOSES : DEPOSIT_PURPOSES;
  const partyKind = PARTY_PURPOSE_KIND[txnForm.purpose];
  const expenseCats = [...EXPENSE_CATEGORIES, ...(settings?.expense_categories || []).filter((c) => !EXPENSE_CATEGORIES.includes(c))];

  const onTxnType = (v) => {
    setTxnForm((f) => ({ ...f, type: v, purpose: v === "withdrawal" ? "Expense" : "Customer Payment", party_id: "", doc_id: "" }));
    setOpenDocs([]);
  };

  const onPurpose = (v) => {
    setTxnForm((f) => ({ ...f, purpose: v, party_id: "", doc_id: "" }));
    setOpenDocs([]);
  };

  const onTxnParty = async (v) => {
    setTxnForm((f) => ({ ...f, party_id: v, doc_id: "" }));
    setOpenDocs([]);
    if (SETTLE_PURPOSES.includes(txnForm.purpose)) {
      try {
        const r = await API.get(partyKind === "customer" ? "/sales" : "/purchases");
        setOpenDocs(r.data.filter((d) => d.status !== "paid" && d.party_id === v));
      } catch (e) {
        toast.error(fmtErr(e));
      }
    }
  };

  const saveTxn = async () => {
    if (!txnForm.account_id) return toast.error("Select an account");
    if (!txnForm.amount || Number(txnForm.amount) <= 0) return toast.error("Enter a valid amount");
    if (partyKind && !txnForm.party_id) return toast.error(`Select a ${partyKind}`);
    setSaving(true);
    try {
      const category = txnForm.purpose === "Expense" ? txnForm.expense_category : txnForm.purpose;
      const r = await API.post("/banking/transactions", {
        account_id: txnForm.account_id, date: txnForm.date, type: txnForm.type,
        amount: Number(txnForm.amount), category, reference: txnForm.reference, notes: txnForm.notes,
        party_id: txnForm.party_id || null,
      });
      if (SETTLE_PURPOSES.includes(txnForm.purpose) && txnForm.doc_id) {
        await API.post(`/banking/transactions/${r.data.id}/link`, { doc_id: txnForm.doc_id });
      }
      toast.success("Transaction recorded");
      setTxnOpen(false);
      setTxnForm({ account_id: "", date: today(), type: "deposit", amount: "", purpose: "Customer Payment", expense_category: "Rent", party_id: "", doc_id: "", reference: "", notes: "" });
      setOpenDocs([]);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const delAccount = async (id) => {
    if (!window.confirm("Delete this account?")) return;
    try {
      await API.delete(`/banking/accounts/${id}`);
      toast.success("Account deleted");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const delTxn = async (id) => {
    if (!window.confirm("Delete this transaction? Balance will be reversed.")) return;
    try {
      await API.delete(`/banking/transactions/${id}`);
      toast.success("Transaction deleted");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const openLink = async (t) => {
    setLinkTxn(t);
    setLinkDocId("");
    try {
      const r = await API.get(t.type === "deposit" ? "/sales" : "/purchases");
      setLinkDocs(r.data.filter((d) => d.status !== "paid"));
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const saveLink = async () => {
    if (!linkDocId) return toast.error("Select an invoice/bill to settle");
    setLinkSaving(true);
    try {
      await API.post(`/banking/transactions/${linkTxn.id}/link`, { doc_id: linkDocId });
      toast.success("Payment recorded and transaction linked");
      setLinkTxn(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLinkSaving(false);
    }
  };

  const openCategorize = (t) => {
    const defaults = t.type === "withdrawal" ? WITHDRAW_PURPOSES : DEPOSIT_PURPOSES;
    const purpose = defaults.includes(t.category) ? t.category : defaults[0];
    setCatTxn(t);
    setCatDocs([]);
    setCatForm({ purpose, expense_category: "Rent", party_id: t.party_id || "", doc_id: "" });
  };

  const catPartyKind = PARTY_PURPOSE_KIND[catForm.purpose];

  const onCatParty = async (v) => {
    setCatForm((f) => ({ ...f, party_id: v, doc_id: "" }));
    setCatDocs([]);
    if (SETTLE_PURPOSES.includes(catForm.purpose)) {
      try {
        const r = await API.get(catPartyKind === "customer" ? "/sales" : "/purchases");
        setCatDocs(r.data.filter((d) => d.status !== "paid" && d.party_id === v));
      } catch (e) {
        toast.error(fmtErr(e));
      }
    }
  };

  const saveCategorize = async () => {
    if (catPartyKind && !catForm.party_id) return toast.error(`Select a ${catPartyKind}`);
    setCatSaving(true);
    try {
      const category = catForm.purpose === "Expense" ? catForm.expense_category : catForm.purpose;
      await API.patch(`/banking/transactions/${catTxn.id}/categorize`, { category, party_id: catForm.party_id || null });
      if (SETTLE_PURPOSES.includes(catForm.purpose) && catForm.doc_id) {
        await API.post(`/banking/transactions/${catTxn.id}/link`, { doc_id: catForm.doc_id });
      }
      toast.success("Transaction categorized");
      setCatTxn(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setCatSaving(false);
    }
  };

  const totalBalance = accounts.reduce((s, a) => s + a.balance, 0);
  const filteredTxns = txns.filter((txn) =>
    (!txnDateFrom || String(txn.date) >= txnDateFrom)
    && (!txnDateTo || String(txn.date) <= txnDateTo)
  );
  const sortedTxns = [...filteredTxns].sort((a, b) => {
    const aValue = txnSort.key === "date" ? String(a.date) : txnSort.key === "debit" ? (a.type === "withdrawal" ? Number(a.amount) : 0) : (a.type === "deposit" ? Number(a.amount) : 0);
    const bValue = txnSort.key === "date" ? String(b.date) : txnSort.key === "debit" ? (b.type === "withdrawal" ? Number(b.amount) : 0) : (b.type === "deposit" ? Number(b.amount) : 0);
    const order = typeof aValue === "string" ? aValue.localeCompare(bValue) : aValue - bValue;
    return order * (txnSort.direction === "asc" ? 1 : -1);
  });
  const sortTransactionsBy = (key) => setTxnSort((current) => ({
    key,
    direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
  }));
  const updateStatementTransactionType = async (txn, type) => {
    if (type === txn.type) return;
    try {
      await API.patch(`/banking/transactions/${txn.id}/type`, { type });
      toast.success("Statement transaction type updated");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  const accountTypeLabel = (type) => ({
    current_account: "Current Account",
    credit_limit: "Credit Limit Account",
    saving_account: "Saving Account",
    bank: "Bank Account",
  }[type] || "Bank Account");

  return (
    <div className="space-y-6" data-testid="banking-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Banking</h1>
          <p className="text-sm text-muted-foreground">Accounts, deposits and withdrawals</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <StatementUpload accounts={accounts} onDone={load} />
          <Dialog open={accOpen} onOpenChange={setAccOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="new-account-button"><Plus size={16} className="mr-1" /> New Account</Button>
            </DialogTrigger>
            <DialogContent data-testid="account-form-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading tracking-tight">New Account</DialogTitle>
                <DialogDescription className="sr-only">Add a current, credit limit, saving, bank, or UPI account</DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2 sm:col-span-2">
                  <Label>Account Name</Label>
                  <Input value={accForm.name} onChange={setAcc("name")} placeholder="e.g. HDFC Current A/c" data-testid="account-name-input" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label>Account Type</Label>
                  <Select value={accForm.account_type} onValueChange={setAcc("account_type")}>
                    <SelectTrigger data-testid="account-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="current_account">Current Account</SelectItem>
                      <SelectItem value="credit_limit">Credit Limit Account</SelectItem>
                      <SelectItem value="saving_account">Saving Account</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label>Account Access</Label>
                  <Select value={accForm.type} onValueChange={setAcc("type")}>
                    <SelectTrigger data-testid="account-access-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank">Bank Account</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {accForm.account_type === "credit_limit" && (
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Sanction Amount (₹)</Label>
                    <Input type="number" min="0" step="any" value={accForm.sanction_amount} onChange={setAcc("sanction_amount")} data-testid="account-sanction-amount-input" />
                  </div>
                )}
                {accForm.type === "upi" ? (
                  <div className="space-y-2 sm:col-span-2">
                    <Label>UPI ID</Label>
                    <Input value={accForm.upi_id} onChange={setAcc("upi_id")} placeholder="business@okhdfc" data-testid="account-upi-input" />
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>Bank Name</Label>
                      <Input value={accForm.bank_name} onChange={setAcc("bank_name")} data-testid="account-bank-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Account Number</Label>
                      <Input value={accForm.account_number} onChange={setAcc("account_number")} data-testid="account-number-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>IFSC</Label>
                      <Input value={accForm.ifsc} onChange={setAcc("ifsc")} data-testid="account-ifsc-input" />
                    </div>
                  </>
                )}
                <div className="space-y-2">
                  <Label>Opening Balance (₹)</Label>
                  <Input type="number" step="any" value={accForm.opening_balance} onChange={setAcc("opening_balance")} data-testid="account-balance-input" />
                  {accForm.account_type === "credit_limit" && <p className="text-xs text-muted-foreground">The opening balance is stored and shown as a negative amount.</p>}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setAccOpen(false)} data-testid="account-cancel-button">Cancel</Button>
                <Button onClick={saveAccount} disabled={saving} data-testid="account-save-button">{saving ? "Saving…" : "Add Account"}</Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={txnOpen} onOpenChange={setTxnOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-transaction-button"><ArrowsDownUp size={16} className="mr-1" /> New Transaction</Button>
            </DialogTrigger>
            <DialogContent data-testid="transaction-form-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading tracking-tight">Record Transaction</DialogTitle>
                <DialogDescription className="sr-only">Record a deposit or withdrawal</DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Account</Label>
                  <Select value={txnForm.account_id} onValueChange={setTxn("account_id")}>
                    <SelectTrigger data-testid="transaction-account-select"><SelectValue placeholder="Select account" /></SelectTrigger>
                    <SelectContent>
                      {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={txnForm.type} onValueChange={onTxnType}>
                    <SelectTrigger data-testid="transaction-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="deposit">Deposit (Money In)</SelectItem>
                      <SelectItem value="withdrawal">Withdrawal (Money Out)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input type="date" value={txnForm.date} onChange={setTxn("date")} data-testid="transaction-date-input" />
                </div>
                <div className="space-y-2">
                  <Label>Amount (₹)</Label>
                  <Input type="number" min="0" step="any" value={txnForm.amount} onChange={setTxn("amount")} data-testid="transaction-amount-input" />
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={txnForm.purpose} onValueChange={onPurpose}>
                    <SelectTrigger data-testid="transaction-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {purposes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {txnForm.purpose === "Expense" && (
                  <div className="space-y-2">
                    <Label>Expense Type</Label>
                    <Select value={txnForm.expense_category} onValueChange={setTxn("expense_category")}>
                      <SelectTrigger data-testid="transaction-expense-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {expenseCats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {partyKind && (
                  <div className="space-y-2">
                    <Label>{partyKind === "customer" ? "Customer" : "Vendor"}</Label>
                    <Select value={txnForm.party_id} onValueChange={onTxnParty}>
                      <SelectTrigger data-testid="transaction-party-select"><SelectValue placeholder={`Select ${partyKind}`} /></SelectTrigger>
                      <SelectContent>
                        {parties.filter((p) => p.type === partyKind).map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {SETTLE_PURPOSES.includes(txnForm.purpose) && txnForm.party_id && (
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Settle open {partyKind === "customer" ? "invoice" : "bill"} (optional)</Label>
                    <Select value={txnForm.doc_id} onValueChange={setTxn("doc_id")}>
                      <SelectTrigger data-testid="transaction-doc-select"><SelectValue placeholder="Choose document to settle — or leave blank for advance/on-account" /></SelectTrigger>
                      <SelectContent>
                        {openDocs.map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            {d.number} · due {inr(d.total - (d.tds_amount || 0) - d.paid_amount)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {openDocs.length === 0 && (
                      <p className="text-xs text-muted-foreground">No open {partyKind === "customer" ? "invoices" : "bills"} for this {partyKind} — recorded as on-account.</p>
                    )}
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Reference</Label>
                  <Input value={txnForm.reference} onChange={setTxn("reference")} placeholder="Cheque / UTR no." data-testid="transaction-reference-input" />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setTxnOpen(false)} data-testid="transaction-cancel-button">Cancel</Button>
                <Button onClick={saveTxn} disabled={saving} data-testid="transaction-save-button">{saving ? "Saving…" : "Save Transaction"}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="accounts-grid">
        <div className="rounded-lg border bg-primary p-5 text-primary-foreground">
          <p className="text-xs font-medium uppercase tracking-wide opacity-80">Total Balance</p>
          <p className="mt-2 font-heading text-2xl font-bold tabular-nums" data-testid="total-balance">{inr(totalBalance)}</p>
        </div>
        {accounts.map((a) => (
          <div key={a.id} className="group rounded-lg border bg-card p-5 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-sm" data-testid={`account-card-${a.name}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-heading font-semibold">
                  {a.name}
                  {a.type === "upi" && <Badge variant="outline" className="ml-2 border-[#5A3DF0]/30 bg-[#5A3DF0]/10 text-[#5A3DF0]">UPI</Badge>}
                </p>
                <p className="text-xs text-muted-foreground">{accountTypeLabel(a.account_type)}{a.account_type === "credit_limit" ? ` · Sanctioned ${inr(a.sanction_amount)}` : ""}</p>
                <p className="text-xs text-muted-foreground">
                  {a.type === "upi"
                    ? a.upi_id || "—"
                    : [a.bank_name, a.account_number && `••${a.account_number.slice(-4)}`].filter(Boolean).join(" · ") || "—"}
                </p>
              </div>
              <Button variant="ghost" size="icon" className="opacity-0 transition-opacity group-hover:opacity-100" onClick={() => delAccount(a.id)} data-testid={`account-delete-${a.name}`}>
                <Trash size={15} className="text-muted-foreground" />
              </Button>
            </div>
            <p className={`mt-3 font-heading text-xl font-bold tabular-nums ${a.balance < 0 ? "text-[#E02424]" : ""}`}>{inr(a.balance)}</p>
          </div>
        ))}
      </div>

      <div className="rounded-lg border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
          <h2 className="font-heading text-base font-semibold tracking-tight">Transactions</h2>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex items-center gap-2">
              <Label htmlFor="transactions-account-filter" className="whitespace-nowrap text-sm text-muted-foreground">Bank account</Label>
              <Select value={txnAccountId} onValueChange={setTxnAccountId}>
                <SelectTrigger id="transactions-account-filter" className="w-[220px]" data-testid="transactions-account-filter">
                  <SelectValue placeholder="Select bank account" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All accounts</SelectItem>
                  {accounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>{account.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <div className="grid gap-1">
                <Label htmlFor="transactions-date-from" className="text-xs text-muted-foreground">From</Label>
                <Input id="transactions-date-from" type="date" value={txnDateFrom} onChange={(event) => setTxnDateFrom(event.target.value)} className="w-[150px]" data-testid="transactions-date-from" />
              </div>
              <div className="grid gap-1">
                <Label htmlFor="transactions-date-to" className="text-xs text-muted-foreground">To</Label>
                <Input id="transactions-date-to" type="date" value={txnDateTo} onChange={(event) => setTxnDateTo(event.target.value)} className="w-[150px]" data-testid="transactions-date-to" />
              </div>
              {(txnDateFrom || txnDateTo) && (
                <Button variant="outline" size="sm" onClick={() => { setTxnDateFrom(""); setTxnDateTo(""); }} data-testid="transactions-date-clear">
                  Clear dates
                </Button>
              )}
            </div>
          </div>
        </div>
        {filteredTxns.length === 0 ? (
          <p className="p-10 text-center text-sm text-muted-foreground" data-testid="empty-state">
            {txns.length === 0
              ? (txnAccountId === "all" ? "No transactions yet." : "No transactions for this bank account.")
              : "No transactions found for the selected filters."}
          </p>
        ) : (
          <div className="overflow-x-auto">
          <Table data-testid="transactions-table" className="min-w-[640px]">
            <TableHeader>
              <TableRow>
                <TableHead><button type="button" onClick={() => sortTransactionsBy("date")} className="font-medium hover:text-foreground">Date {txnSort.key === "date" ? (txnSort.direction === "asc" ? "↑" : "↓") : ""}</button></TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Narration</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead className="text-right"><button type="button" onClick={() => sortTransactionsBy("debit")} className="font-medium hover:text-foreground">Debit {txnSort.key === "debit" ? (txnSort.direction === "asc" ? "↑" : "↓") : ""}</button></TableHead>
                <TableHead className="text-right"><button type="button" onClick={() => sortTransactionsBy("credit")} className="font-medium hover:text-foreground">Credit {txnSort.key === "credit" ? (txnSort.direction === "asc" ? "↑" : "↓") : ""}</button></TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedTxns.map((t) => (
                <TableRow key={t.id} className="transition-colors hover:bg-muted/50" data-testid={`transaction-row-${t.id}`}>
                  <TableCell>{t.date}</TableCell>
                  <TableCell>{t.account_name}</TableCell>
                  <TableCell>
                    {t.category === "Statement Import" && !t.payment_id && !t.expense_id ? (
                      <Select value={t.type} onValueChange={(type) => updateStatementTransactionType(t, type)}>
                        <SelectTrigger className="h-8 w-[130px]" aria-label={`Change transaction type for ${t.date}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="deposit">Deposit (Credit)</SelectItem>
                          <SelectItem value="withdrawal">Withdrawal (Debit)</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant="outline" className={t.type === "deposit" ? "border-[#008A52]/30 bg-[#008A52]/10 text-[#008A52]" : "border-[#E02424]/30 bg-[#E02424]/10 text-[#E02424]"}>
                        {t.type}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{t.category}{t.party_name ? <span className="text-muted-foreground"> · {t.party_name}</span> : ""}</TableCell>
                  <TableCell className="max-w-[240px] truncate" title={t.notes || ""}>{t.notes || "—"}</TableCell>
                  <TableCell>{t.reference || "—"}</TableCell>
                  <TableCell className="text-right font-medium tabular-nums text-[#E02424]">
                    {t.type === "withdrawal" ? inr(t.amount) : "—"}
                  </TableCell>
                  <TableCell className="text-right font-medium tabular-nums text-[#008A52]">
                    {t.type === "deposit" ? inr(t.amount) : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end">
                      {!t.payment_id && !t.expense_id && (
                        <>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => openCategorize(t)} data-testid={`transaction-categorize-${t.id}`}>
                            Categorize
                          </Button>
                          <Button variant="ghost" size="icon" title="Link to invoice/bill" onClick={() => openLink(t)} data-testid={`transaction-link-${t.id}`}>
                            <LinkSimple size={15} className="text-muted-foreground" />
                          </Button>
                        </>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => delTxn(t.id)} data-testid={`transaction-delete-${t.id}`}>
                        <Trash size={15} className="text-muted-foreground" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        )}
      </div>

      <Dialog open={!!linkTxn} onOpenChange={(v) => !v && setLinkTxn(null)}>
        <DialogContent className="max-w-md" data-testid="link-txn-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">
              Link to {linkTxn?.type === "deposit" ? "Sales Invoice" : "Purchase Bill"}
            </DialogTitle>
            <DialogDescription className="sr-only">Record this transaction as a payment against an open invoice or bill</DialogDescription>
          </DialogHeader>
          {linkTxn && (
            <p className="rounded-md border bg-muted/40 px-3 py-2 text-sm" data-testid="link-txn-summary">
              {linkTxn.date} · {linkTxn.account_name} · <span className="font-semibold tabular-nums">{inr(linkTxn.amount)}</span> ({linkTxn.type})
            </p>
          )}
          <div className="space-y-2">
            <Label>{linkTxn?.type === "deposit" ? "Sales Invoice" : "Purchase Bill"}</Label>
            <Select value={linkDocId} onValueChange={setLinkDocId}>
              <SelectTrigger data-testid="link-doc-select">
                <SelectValue placeholder="Select open document" />
              </SelectTrigger>
              <SelectContent>
                {linkDocs.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.number} · {d.party_name} · due {inr(d.total - (d.tds_amount || 0) - d.paid_amount)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {linkDocs.length === 0 && (
              <p className="text-xs text-muted-foreground">No open documents with a balance due.</p>
            )}
            <p className="text-xs text-muted-foreground">
              The full transaction amount is recorded as the payment, and the transaction can no longer be deleted afterwards.
            </p>
          </div>
          <Button className="w-full" onClick={saveLink} disabled={linkSaving} data-testid="link-confirm-button">
            {linkSaving ? "Linking…" : "Record Payment & Link"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!catTxn} onOpenChange={(v) => !v && setCatTxn(null)}>
        <DialogContent className="max-w-md" data-testid="categorize-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Categorize Transaction</DialogTitle>
            <DialogDescription className="sr-only">Assign a category, party and optionally settle an open document</DialogDescription>
          </DialogHeader>
          {catTxn && (
            <p className="rounded-md border bg-muted/40 px-3 py-2 text-sm" data-testid="categorize-summary">
              {catTxn.date} · {catTxn.account_name} · <span className="font-semibold tabular-nums">{inr(catTxn.amount)}</span> ({catTxn.type})
            </p>
          )}
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={catForm.purpose} onValueChange={(v) => { setCatForm((f) => ({ ...f, purpose: v, party_id: "", doc_id: "" })); setCatDocs([]); }}>
                <SelectTrigger data-testid="categorize-purpose-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(catTxn?.type === "withdrawal" ? WITHDRAW_PURPOSES : DEPOSIT_PURPOSES).map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {catForm.purpose === "Expense" && (
              <div className="space-y-2">
                <Label>Expense Type</Label>
                <Select value={catForm.expense_category} onValueChange={(v) => setCatForm((f) => ({ ...f, expense_category: v }))}>
                  <SelectTrigger data-testid="categorize-expense-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {expenseCats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {catPartyKind && (
              <div className="space-y-2">
                <Label>{catPartyKind === "customer" ? "Customer" : "Vendor"}</Label>
                <Select value={catForm.party_id} onValueChange={onCatParty}>
                  <SelectTrigger data-testid="categorize-party-select"><SelectValue placeholder={`Select ${catPartyKind}`} /></SelectTrigger>
                  <SelectContent>
                    {parties.filter((p) => p.type === catPartyKind).map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {SETTLE_PURPOSES.includes(catForm.purpose) && catForm.party_id && (
              <div className="space-y-2">
                <Label>Settle open {catPartyKind === "customer" ? "invoice" : "bill"} (optional)</Label>
                <Select value={catForm.doc_id} onValueChange={(v) => setCatForm((f) => ({ ...f, doc_id: v }))}>
                  <SelectTrigger data-testid="categorize-doc-select"><SelectValue placeholder="Leave blank for on-account" /></SelectTrigger>
                  <SelectContent>
                    {catDocs.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.number} · due {inr(d.total - (d.tds_amount || 0) - d.paid_amount)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <Button className="w-full" onClick={saveCategorize} disabled={catSaving} data-testid="categorize-save-button">
            {catSaving ? "Saving…" : "Save Category"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
